<template>

  <div  class="scheduler-wrapper">

	<div ref="cornerRef">	<Corner :printProject="printProject"
            :configs="props.configs" :projectFilter="projectFilter" @setProjectFilter="handleSetProjectFilter" />
    </div>
    <div ref="sidebarRef" :style="{height: printProject ? 'fit-content' : 'auto'}" :class="{printview: printProject}" class="sidebar" id="sidebar">
		<ProjectHeader
		    v-for="(p, i) in displayProjects"
		    :project="p"
	         :isprintView="printProject || false"			
			:users="props.users"
			:bookings="displayBookings"
			:isopen="view.isOpenProject(p.id)"
			:resourceFilter="resourceFilter[p.id]"
			@updateResourceFilter="handleUpdateResourceFilter"
			@update="updateProjectOpenstates" 
			@print-view="handlePrintAction"
		  />
	</div>
    <div ref="timesheetRef" class="main-scroll-area" id="main-scroll-area">
      <Timeaxis :key="`${startDate.getTime()}-${endDate.getTime()}`" :printProject="printProject" :startDate="startDate" :endDate="endDate" @deselect="closeSelection" />
	  <div class="timesheet" id="timesheet">
	    <ProjectTimesheet
		  :key="`${startDate.getTime()}-${endDate.getTime()}`"
		  :startDate="startDate"
		  :endDate="endDate"
	      v-for="p in displayProjects"
	      :project="p"
	      :isprintView="(printProject && printProject.id === p.id) || false"
	      :projects="props.projects"
	      :users="props.users"
	      :bookings="displayBookings"
	      :isopen="view.isOpenProject(p.id)"
	      :resourceFilter="resourceFilter[p.id]"
	      :configs="props.configs"
	      @bookingDone="handleBookingDone"
	      @projectChangeDone="projectChangeDone"
	      @cancel="handleCancel"
	      @deselect="deselect"
	    />
	    <HighlightDays :startDate="startDate" :endDate="endDate" :isprintView="printProject !== null" :configs="props.configs" />
	    <div v-if="!printProject" id="today-line" class="today-line"> </div>
	    <div v-if="!printProject" id="selection-layer" class="selection-layer"></div>
	  </div>
    </div>
	
	<Callout id="projecttimePopup" :target="popupTarget" :show="showPopup" @close="closePopup" >
	  <div>
	      <p>{{popupType === 0 ? "Projektzeitraum" : "Buchungszeitraum"}} ändern</p>
		  <DateInput label="Start" :modelValue="entityStartDate" @datechange="handleStartDateChange"  />
		  <div class="spacer"/>
		  <DateInput label="Ende" :modelValue="entityEndDate" @datechange="handleEndDateChange"/>
		  <div class="spacer"/>
		  <button
		    class="w-full flex items-center space-x-2 text-center timin_text_center px-3 py-2 bg-green-500 hover:bg-green-600 text-black"
		    @click="handleEntityChange()"
		  >
		    <span>Anwenden</span>
		  </button>

	  </div>
	</Callout>
	
	<BookingSelectionPopup ref="selectionPopupRef"
	                      :projects="displayProjects"
						  :users="props.users"
						  :bookings="displayBookings"
						  :xScale="xScaleRef"
						  :days="daysRef"
						  @bookingDone="handleBookingDone"
						  @dateupdate="handleDateUpdate" />
						  
    <ConfirmModal ref="confirmModal" :message="modalMessage" :colsitems="modalColsItems" :items="modalItems" />

	<div v-if="printProject" class="print-footer">
        <div class="footer-content">
            <button type="button" class="footer-btn" @click="handleDownloadPdf(printProject)">
                <Printer :size="24" />
                <span>PDF Drucken</span>
            </button>
            <button type="button" class="footer-btn secondary" @click="handleRestorePlanner">
                <ArrowBigLeft :size="24" />
                <span>Zurück</span>
            </button>
        </div>
    </div>
  </div>

  <div v-if="isGenerating" class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <Card class="w-[300px] p-6 text-center">
      <div class="mb-4 font-bold text-sm">PDF wird generiert...</div>
      
      <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
        <div 
          class="bg-blue-600 h-full transition-all duration-300 ease-out"
          :style="{ width: progress + '%' }"
        ></div>
      </div>
      
      <div class="mt-2 text-xs text-muted-foreground">{{ Math.round(progress) }}%</div>
    </Card>
  </div>

</template>

<script setup lang="ts">
import {onMounted, computed, ref, provide } from 'vue';
import {useViewStore} from "@/stores/viewStore"
import * as d3 from 'd3';
import {generateTiledPdf, sortProjects, removeSelections, computeResourceFilter, scrollToToday, sendRequest, prepProject, prepBooking, getBavariaHolidaysBetween, dayMs, getWidthPerDay, rowHeight, box2int, germanToISO, germanToISODate, setDim, getProjectFromRow, getXDateUTC, formatDateDDMMYYYY} from "./planner.js";
import BookingSelectionPopup from './BookingSelectionPopup.vue'
import Corner from './Corner.vue'
import Timeaxis from './Timeaxis.vue'
import ProjectHeader from './ProjectHeader.vue'
import ProjectTimesheet from './ProjectTimesheet.vue'
import HighlightDays from './HighlightDays.vue'
import ConfirmModal from './ConfirmModal.vue'
import DateInput from "./DateInput.vue"
import Callout from './Callout.vue'
import { Card } from "@/components/ui/card"
import { Printer, ArrowBigLeft } from "lucide-vue-next"
import { colscollisions } from '../timintables/colscollisions'
import { POPUP_BUS } from './popupBus'

const props = defineProps({
  projects: { type: Array, required: true },
  bookings: { type: Array, required: true },
  users: { type: Array, required: true },
  configs: { type: Array, required: true },
});

const cornerRef = ref(null);
const sidebarRef = ref(null);
const timesheetRef = ref(null)

const isGenerating = ref(false);
const progress = ref(0); // 0 to 100

const handleDownloadPdf = async (project) => {
  if (!timesheetRef.value) return;
  
  isGenerating.value = true;
  progress.value = 0; // Reset progress
  
  try {
    const { default: html2canvas } = await import('html2canvas');
    const { jsPDF } = await import('jspdf');

    // Pass a callback to update progress
    await generateTiledPdf(project, 
      timesheetRef.value, 
      cornerRef.value, 
      sidebarRef.value, 
      html2canvas, 
      jsPDF,
      (p) => { progress.value = p; } // New callback argument
    );
    
  } catch (error) {
    console.error("PDF Generation failed:", error);
  } finally {
    // Small delay so the user sees "100%" before it vanishes
    setTimeout(() => {
      isGenerating.value = false;
      progress.value = 0;
    }, 500);
  }
};

const view = useViewStore();
view.setCountActiveUsers(props.users.filter(u => u.role_id > 0 && u.is_active === 1).length)

const selectionPopupRef = ref(null)
const confirmModal = ref(null)
const modalColsItems = ref(null)
const modalItems = ref([])
const modalMessage = ref("Bestätigen")
const projectFilter = ref({all: view.showAllBookings(), active: view.showActiveProjects(), planned: view.showPlannedProjects()})
const resourceFilter = ref(computeResourceFilter(view, props.projects, props.users, props.bookings));
const xScaleRef = ref(null);
const daysRef = ref([]);

const printProject = ref(null)
const printStart = ref(null)
const printEnd = ref(null)

const startDateStr = props.configs.find(c => c.name === "startdate").value;
const endDateStr = props.configs.find(c => c.name === "enddate").value;

const initialStartDate = new Date(startDateStr + "T00:00:00");
const initialEndDate = new Date(endDateStr   + "T00:00:00");

const startDate = ref<Date>(initialStartDate)
const endDate   = ref<Date>(initialEndDate)

import { watch, nextTick } from "vue";

watch(
  [printStart, printEnd],
  async ([ps, pe]) => {
	console.log("watch printStart, printEnd=" + ps + " " + pe)
    if (ps && pe) {
      // If ps is already a Date object, don't append the time string
      startDate.value = ps instanceof Date ? ps : new Date(germanToISO(ps) + "T00:00:00");
      endDate.value   = pe instanceof Date ? pe : new Date(germanToISO(pe) + "T00:00:00");
    } else {
      startDate.value = initialStartDate;
      endDate.value   = initialEndDate;
    }
	console.log("watch new values=" + startDate.value + " " + endDate.value)

    await nextTick();
    renderTimesheet();
  }
);

const entityStartDate = ref(formatDateDDMMYYYY(new Date()));
const entityEndDate = ref(formatDateDDMMYYYY(new Date()));

const uid = ref(document.getElementById('app').dataset.uid)
console.log("uid=" + uid.value)
const localProjects = ref([...props.projects]);
const displayProjects = computed(() => [...sortProjects(localProjects.value.filter(p => projectFilter.value.active  && p.status === 0 && (printProject.value ? printProject.value.id === p.id : true))).map(p => prepProject(p)),
                                        ...sortProjects(localProjects.value.filter(p => projectFilter.value.planned && p.status === 2)).map(p => prepProject(p))]);

const localBookings = ref([...props.bookings]);
const displayBookings = computed(() =>
  localBookings.value
    .filter(b => projectFilter.value.all || b.user_id.toString() === uid.value.toString())
    .map(b => prepBooking(b))
);


const emit = defineEmits(['update:bookings', 'update:projects', 'reload'])

function handleSetProjectFilter(all, active, planned) {
  view.setProjectFilter(all, active, planned); 
}

function updateProjectOpenstates(pid) {
	view.toggleProject(pid);
}

function handleUpdateResourceFilter(pid, rf) {
  console.log("handleUpdateResourceFilter pid=" + pid + " resourceFilter=" + JSON.stringify(rf));
  resourceFilter.value[pid].selection = rf.selection;
  resourceFilter.value[pid].individual = rf.individual; 
  view.setResourceFilter(pid, rf.selection, rf.individual)
}


const showPopup = ref(false)
const popupTarget = ref<HTMLElement | null>(null)
const popupType = ref(null);
const popupEntity = ref(null);

function openPopupAt(el: HTMLElement, entity) {
  console.log("openPopupAt id=" + el.id + "=" + JSON.stringify(entity))
  popupTarget.value = el
  showPopup.value = true
  popupType.value = entity.isproject ? 0 : 1;
  entityStartDate.value = formatDateDDMMYYYY(entity.start);
  
  entityEndDate.value = formatDateDDMMYYYY(entity.end);
  popupEntity.value = entity;
}

function closePopup() {
  showPopup.value = false
  closeSelection();		
}

// Provide the API for children
provide(POPUP_BUS, { openPopupAt, closePopup })

function handleStartDateChange(d) {
  entityStartDate.value = d;
}

function handleEndDateChange(d) {
  entityEndDate.value = d;
}

function handleEntityChange() {
  const params = {
	  isproject: popupEntity.value.isproject,
	  id: popupEntity.value.id,
	  booking_action: popupEntity.value.id,
	  selected_pid: popupEntity.value.id, 
	  selected_bid: popupEntity.value.id,
	  firstDay: entityStartDate.value,
	  lastDay: entityEndDate.value,
	  selectedResources: [popupEntity.value.uid],
	  shift: false,
	  changedir: false,
  };
  console.log("handleEntityChange=" + JSON.stringify(params))
  if (popupEntity.value.isproject) {
	projectChangeDone(params);		
  } else {
    handleBookingDone(params);
  }
  closePopup();
}

function updatePlanner() {
	closeSelection();	
	//emit('reload');
}

function handleBookingDone(params) {
  sendRequest('/update_bookings', params, function(bookings) {
	localBookings.value = bookings
    updatePlanner();
  }, function(response) {
    alert(response.error);
    updatePlanner();
    if (pHeaderKeys[params.selected_pid]) {
    }
  });
}

function projectChangeDone(params) {
	sendRequest('/change_project_date', params, function(response) {
		localProjects.value = response["projects"]
		let affected_bookings = response["affected_bookings"]
		modalMessage.value = "Folgende Buchungen sind betroffen"
		modalColsItems.value = colscollisions
		modalItems.value = affected_bookings
		console.log("affected_bookings=" + JSON.stringify(affected_bookings))
		if (affected_bookings.length > 0) {
			confirmModal.value.open();			
		}
        updatePlanner()
	}, function(response) {
		alert(response['error']);
        updatePlanner()
	});
}

function handleDateUpdate(startdate, enddate) {
	console.log("handleDateUpdate startdate, enddate=" + startdate +  " " +  enddate);
	
	let startDate = germanToISODate(startdate);
	let endDate   = germanToISODate(enddate);
	 
	let rect = document.querySelector(".selection-rectangle");
	if (!rect) {
		return;
	}

	let newleft = parseFloat(rect.style.left);
	let newwidth = parseFloat(rect.style.width);
	
	const selectionLayer = document.getElementById('selection-layer');
	const containerRect = selectionLayer.getBoundingClientRect();
	console.log("x(startDate)=" + x(startDate) + " x(endDate)=" + x(endDate))
	let newWidth = Math.round(x(endDate) - x(startDate)) + getWidthPerDay(printProject.value !== null);
	setDim(rect, newleft, parseFloat(rect.style.top), newWidth, parseFloat(rect.style.height));	
}

function handleCancel() {
   console.log("reload in D3Planner")
   emit('reload')
}

function deselect() {
  removeSelections()
  closeSelection();	
}

function closeSelection() {
  removeSelections()
  document.getElementById("timesheet").classList.remove("disable-selection");
  const selectionLayer = document.getElementById('selection-layer');
  if (!selectionLayer) {
	return
  }
  let sel = selectionLayer.querySelector('.selection-rectangle');
  if (sel) {
    sel.remove();
	selectionPopupRef.value?.handleSelectionClose();
  }		
}  

function renderTimesheet() {
	console.log("startDateStr=" + startDateStr)
	console.log("startDate=" + startDate.value)
	console.log("endDate=" + endDate.value)

	let widthPerDay= getWidthPerDay(printProject.value !== null)
	const totalDays = (endDate.value.getTime() - startDate.value.getTime()) / dayMs;

    const x = d3.scaleUtc()
  .domain([startDate.value, endDate.value])
  .range([0, totalDays * widthPerDay]);

    xScaleRef.value = x;

	let dayWidth = Math.round(x(new Date(2024, 0, 2)) - x(new Date(2024, 0, 1)));

	const timesheet = document.getElementById("timesheet");
	timesheet.style.width = `${totalDays * widthPerDay}px`;

	const sidebar = document.getElementById("sidebar");
	const mainScrollArea = document.getElementById("main-scroll-area");

    const days = d3.timeDay.range(startDate.value, endDate.value);
	daysRef.value = days;

	//console.log("holidays=" + JSON.stringify(holidays))


	const selectionLayer = document.getElementById('selection-layer');

	// add a vertical line for today's date'
	if (!printProject.value) {
	  const todayLine = document.getElementById("today-line");
	  todayLine.style.left = `${x(new Date())}px`;
	}


	let isDraggingRectangle = false;
	
	document.addEventListener("keydown", e => {
		if (e.code === "Escape") {
			closeSelection();			
		}
	})

	mainScrollArea.addEventListener("scroll", () => {
	  sidebar.scrollTop = mainScrollArea.scrollTop;
	});

	let longPressTimer: number
	let startX = 0
	let startY = 0
	const LONG_PRESS_THRESHOLD = 500 // ms
	const MOVE_THRESHOLD = 10 // pixels

	timesheet.addEventListener("pointerdown", e => {
	  if (e.target.closest(".task") || e.target.closest(".selection-rectangle") || e.target.closest(".project-row")) return;

	  document.querySelectorAll(".task.editable").forEach(t => t.classList.remove("editable"));

	  startX = e.clientX
	  startY = e.clientY
	  let moved = false
	  let longPressTriggered = false

	  // Start long press timer
	  longPressTimer = window.setTimeout(() => {
	    longPressTriggered = true
	    closeSelection()
	    startSelectionRectangle(e)
	  }, LONG_PRESS_THRESHOLD)

	  const onPointerMove = (ev: PointerEvent) => {
	    const dx = ev.clientX - startX
	    const dy = ev.clientY - startY
	    if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
	      moved = true
	      clearTimeout(longPressTimer)
	      timesheet.removeEventListener("pointermove", onPointerMove)
	      timesheet.removeEventListener("pointerup", onPointerUp)
	    }
	  }

	  const onPointerUp = (ev: PointerEvent) => {
	    clearTimeout(longPressTimer)
	    timesheet.removeEventListener("pointermove", onPointerMove)
	    timesheet.removeEventListener("pointerup", onPointerUp)

	    if (!longPressTriggered && !moved) {
	      // Short tap / click
	      closeSelection()
	      startSelectionRectangle(ev, true)
	    }
	    // else: either long press triggered or user dragged → do nothing
	  }

	  timesheet.addEventListener("pointermove", onPointerMove)
	  timesheet.addEventListener("pointerup", onPointerUp)
	})


	// Assume `closeSelection()` is your deselect callback
	function enableTouchDeselect(timesheetEl: HTMLElement) {
	  const onTouchStart = (e: TouchEvent) => {
	    // Only trigger if selection is active and user taps outside rectangle
	    const target = e.target as HTMLElement
	    if (!target.closest(".selection-rectangle")) {
	      closeSelection()
	      removeListeners()
	    }
	  }

	  const removeListeners = () => {
	    timesheetEl.removeEventListener("touchstart", onTouchStart)
	  }

	  timesheetEl.addEventListener("touchstart", onTouchStart)
	}

	// Usage: when selection is active on touch devices
	function onSelectionActivated() {
	  if ("ontouchstart" in window) {
	    enableTouchDeselect(timesheet)
	  }
	}

		
	timesheet.addEventListener('contextmenu', e => {
	  e.preventDefault(); // block long-press context menu
	});

	function startSelectionRectangle(e, immediate = false) {
	  enableTouchDeselect(timesheet);
	  closePopup();
	  timesheet.classList.add("disable-selection");
	  const rect = document.createElement("div");
	  const openPopup = () => {
		let b = box2int({x: rect.style.left,
			             y: rect.style.top,
						 width: rect.style.width,
						 height: rect.style.height });
	    console.log("b=" + JSON.stringify(b));
		selectionPopupRef.value?.handleSelectionComplete(b);
	  } 

	  rect.removeEventListener("click", openPopup);
	  rect.className = "selection-rectangle";
	  selectionLayer.appendChild(rect);

	  const containerRect = selectionLayer.getBoundingClientRect();
	  const startX = e.clientX - containerRect.left;
	  const startY = e.clientY - containerRect.top;
	  console.log("startX=" + startX + " startY=" + startY)

	  const snappedLeft = Math.round(startX / dayWidth) * dayWidth;
	  const snappedTop = Math.round(startY / rowHeight) * rowHeight;

	  setDim(rect, snappedLeft, snappedTop, dayWidth, rowHeight);

	  
	  if (immediate) {
		//console.log("x.invert=" + getXDateUTC(x, startX));	
		let clickDate = getXDateUTC(x, startX);
		//console.log("clickDate=" + clickDate)
		let p = getProjectFromRow(e, displayProjects.value);
		if (!p || clickDate < p.start || clickDate > p.end) {
			closeSelection();
			return;
		}
		openPopup();
		return;
	  } else {
		isDraggingRectangle = true;		
	  }
	  

	  const updateRect = (clientX, clientY) => {
	    const x = clientX - containerRect.left;
	    const y = clientY - containerRect.top;

	    const left = Math.min(startX, x);
	    const top = Math.min(startY, y);
	    const width = Math.abs(x - startX);
	    const height = Math.abs(y - startY);

	    setDim(rect, left, top, width, height)
	  };

	  const onMove = e2 => updateRect(e2.clientX, e2.clientY);

	  const onUp = e2 => {
	    timesheet.removeEventListener("pointermove", onMove);
	    timesheet.removeEventListener("pointerup", onUp);
	    snapSelectionRectangleToGrid(rect);
	    rect.addEventListener("click", openPopup);
	  };

	  timesheet.addEventListener("pointermove", onMove);
	  timesheet.addEventListener("pointerup", onUp);

	  function snapSelectionRectangleToGrid(rect) {
	    const left = parseFloat(rect.style.left);
	    const top = parseFloat(rect.style.top);
	    const width = parseFloat(rect.style.width);
	    const height = parseFloat(rect.style.height);

	    const snappedLeft = Math.round(left / dayWidth) * dayWidth;
	    const snappedTop = Math.round(top / rowHeight) * rowHeight;
	    const snappedRight = Math.round((left + width) / dayWidth) * dayWidth;
	    const snappedBottom = Math.round((top + height) / rowHeight) * rowHeight;

	    setDim(rect, snappedLeft, snappedTop, snappedRight - snappedLeft, snappedBottom - snappedTop);
	  	  
	    addResizeHandlesTo(rect);           // You can implement this later
	    isDraggingRectangle = false;
	    openPopup();
	  }

	  function addResizeHandlesTo(rect) {
	    const positions = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

	    for (const pos of positions) {
	      const handle = document.createElement("div");
	      handle.className = `resize-handle ${pos}`;
	      rect.appendChild(handle);

	      setupResizeHandle(handle, rect, pos);
	    }
      }

	  function setupResizeHandle(handle, rect, position) {
	    handle.addEventListener("pointerdown", e => {
	      e.stopPropagation();
	      e.preventDefault();

	      const containerRect = selectionLayer.getBoundingClientRect();
	      const startX = e.clientX;
	      const startY = e.clientY;
	      const startLeft = parseFloat(rect.style.left);
	      const startTop = parseFloat(rect.style.top);
	      const startWidth = parseFloat(rect.style.width);
	      const startHeight = parseFloat(rect.style.height);

		  const onMove = e2 => {
		  const dx = e2.clientX - startX;
		  const dy = e2.clientY - startY;

		  let newLeft = startLeft;
		  let newTop = startTop;
		  let newWidth = startWidth;
		  let newHeight = startHeight;

		  if (position.includes("e")) {
		    newWidth = startWidth + dx;
		  }
		  if (position.includes("s")) {
		    newHeight = startHeight + dy;
		  }
		  if (position.includes("w")) {
		    newLeft = startLeft + dx;
		    newWidth = startWidth - dx;
		  }
		  if (position.includes("n")) {
		    newTop = startTop + dy;
		    newHeight = startHeight - dy;
		  }

		  // Snap to grid (live snapping)
		  newLeft = Math.round(newLeft / dayWidth) * dayWidth;
		  newTop = Math.round(newTop / rowHeight) * rowHeight;
		  newWidth = Math.round(newWidth / dayWidth) * dayWidth;
		  newHeight = Math.round(newHeight / rowHeight) * rowHeight;

		  newWidth = Math.max(dayWidth, newWidth);
		  newHeight = Math.max(rowHeight, newHeight);

		  setDim(rect, newLeft, newTop, newWidth, newHeight);
		  };

	      const onUp = () => {
	        document.removeEventListener("pointermove", onMove);
	        document.removeEventListener("pointerup", onUp);
	        snapSelectionRectangleToGrid(rect);
	      };

	      document.addEventListener("pointermove", onMove);
	      document.addEventListener("pointerup", onUp);
	    });
	  }
	}
	
	scrollToToday(x);
}


function handlePrintAction(project, start, end) {
   console.log("printview p=" + project.name + " start=" + start + " end=" + end)
   printProject.value = project;
   printStart.value = start;
   printEnd.value = end;
}

function handleRestorePlanner() {
   printProject.value = null;
   printStart.value = null;
   printEnd.value = null;
}


onMounted(() => {renderTimesheet();});
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: sans-serif;
}

.scheduler-wrapper {
  display: grid;
  grid-template-rows: auto 1fr;
  width: fit-content;
  height: 100vh;
  position: relative;
  overflow: visible;
}

.corner {
  grid-column: 1;
  grid-row: 1;
  background: #f0f0f0;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  height: 96px;
}

#sidebar {
  grid-column: 1;
  grid-row: 2;
  overflow-y: auto;
  background: #f0f0f0;
  border-right: 1px solid #ccc;
  font-size: 12px;
}

#sidebar.printview {
  background: white !important;
}



.sidebar-group {
  font-weight: bold;
  background: #ddd;
  padding: 4px;
  border-bottom: 1px solid #bbb;
  cursor: pointer;
  user-select: none;
}

.sidebar-resource {
  padding-left: 12px;
  box-sizing: border-box;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-scroll-area {
  grid-column: 2;
  grid-row: 1 / span 2;
  overflow: auto;
  position: relative;
  background-color: white;
}

.main-scroll-area {
  scrollbar-width: auto;           /* "auto" | "thin" */
  scrollbar-color: #999 #e5e5e5;   /* thumb | track */
}

/* WebKit (Chrome, Edge, Safari) */
.main-scroll-area::-webkit-scrollbar {
  height: 66px;   /* horizontal scrollbar thickness only */
  width: auto;    /* vertical stays default */
}

.main-scroll-area::-webkit-scrollbar-track {
  background: #e5e5e5;
}

.main-scroll-area::-webkit-scrollbar-thumb {
  background: #999;
  border-radius: 8px;
  border: 3px solid #e5e5e5; /* optional padding effect */
}

.main-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #777;
}
.timesheet {
  position: relative;
  width: max-content;
}

.today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: red;
  z-index: 2;
  pointer-events: none;
}

.corner {
  grid-column: 1;
  grid-row: 1;
  background: #f0f0f0;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
}

#today-button {
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

.task.editable .resize-handle {
  display: block;
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px; /* bigger touch area */
  margin: 0 -10px; /* overlap into task */
  z-index: 2;
  background: transparent;
  cursor: ew-resize;
  display: none;
  touch-action: none; /* prevent unwanted pinch-zoom or pan */
}

.resize-handle::before {
  content: "";
  position: absolute;
  top: 25%;
  bottom: 25%;
  width: 4px;
  background-color: #666;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
}

.resize-handle.left {
  left: -3px;
}

.resize-handle.right {
  right: -3px;
}

.task.editable .drag-handle {
  display: block;
}

.drag-handle {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background-color: #444;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  cursor: grab;
  z-index: 10;
  touch-action: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.selection-layer {
  position: absolute;  /* or relative if it's already inside a positioned container */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: visible;
  z-index: 1000;  /* lower than .selection-rectangle (z: 5 or 10) */
  pointer-events: none; /* so other things like tasks remain clickable */
}


.selection-rectangle {
  position: absolute;
  border: 2px dashed #007bff;
  background-color: rgba(0, 123, 255, 0.1);
  pointer-events: auto;
  z-index: 1000;
}

.selection-rectangle .resize-handle {
  position: absolute;
  width: 16px;
  height: 16px;
  background: red;
  border: 2px solid white;
  border-radius: 4px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
  z-index: 1010;
  display: block;
  pointer-events: auto;
}

.timesheet.disable-selection {
  touch-action: none;
  user-select: none;
}

.highlight-bar {
  border: 3px solid black !important;
}

.print-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  /* Match this width to your sidebar/corner width */
  width: 250px; 
  height: 80px;
  background: #f8f9fa;
  border-top: 2px solid #ccc;
  border-right: 1px solid #ccc;
  z-index: 9999; /* Ensure it's above everything */
  display: flex;
  align-items: center;
  padding: 0 15px;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
}

.footer-content {
  display: flex;
  gap: 12px;
}

.footer-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #22c55e; /* green-500 */
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 10px;
  font-weight: bold;
  transition: transform 0.1s;
}

.footer-btn:active {
  transform: scale(0.95);
}

.footer-btn.secondary {
  background: #64748b; /* slate-500 */
}


/* Positioning */
.selection-rectangle .resize-handle.nw { top: -12px; left: -12px; cursor: nwse-resize; }
.selection-rectangle .resize-handle.ne { top: -12px; right: -12px; cursor: nesw-resize; }
.selection-rectangle .resize-handle.sw { top: 100%; left: -12px; cursor: nesw-resize; }
.selection-rectangle .resize-handle.se { top: 100%; right: -12px; cursor: nwse-resize; }

.selection-rectangle .resize-handle.n  { top: -12px; left: 55%; transform: translateX(-50%); cursor: ns-resize; }
.selection-rectangle .resize-handle.s  { top: 100%; left: 55%; transform: translateX(-50%); cursor: ns-resize; }
.selection-rectangle .resize-handle.e  { right: -12px; top: 50%; transform: translateY(-50%); cursor: ew-resize; }
.selection-rectangle .resize-handle.w  { left: -12px; top: 50%; transform: translateY(-50%); cursor: ew-resize; }


</style>
